// @ts-check

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Banner, BlockStack, Button, InlineStack, Layout, Page, Text } from "@shopify/polaris";
import { RefreshIcon } from "@shopify/polaris-icons";
import { keepPreviousData } from "@tanstack/react-query";

import { OrdersDatePicker } from "components/OrdersDatePicker/OrdersDatePicker";
import { TopParametersTable } from "components/TopParametersTable/TopParametersTable";

import { useGlobalContext } from "context/GlobalContext";

import { useAppQuery } from "hooks/use-app-query";

const PER_PAGE = 20;

const PARAMETER_MAP = {
  sources: {
    key: "utm_source",
    nameSingular: "UTM Source",
    namePlural: "UTM Sources",
    calculateReportHref: (parameter) => `/reports/source/${encodeURIComponent(parameter)}`,
  },
  mediums: {
    key: "utm_medium",
    nameSingular: "UTM Medium",
    namePlural: "UTM Mediums",
    calculateReportHref: (parameter) => `/reports/medium/${encodeURIComponent(parameter)}`,
  },
  campaigns: {
    key: "utm_campaign",
    nameSingular: "UTM Campaign",
    namePlural: "UTM Campaigns",
    calculateReportHref: (parameter) => `/reports/campaign/${encodeURIComponent(parameter)}`,
  },
  contents: {
    key: "utm_content",
    nameSingular: "UTM Content",
    namePlural: "UTM Contents",
  },
  terms: {
    key: "utm_term",
    nameSingular: "UTM Term",
    namePlural: "UTM Terms",
  },
};

export default function TopUtmParameters() {
  const navigate = useNavigate();
  const { parameter } = useParams();
  const {
    dates,
    topMetrics,
    refetchMetrics,
  } = useGlobalContext();

  const [page, setPage] = useState(1);

  useEffect(() => {
    return () => {
      setPage(1);
    };
  }, []);

  const topSourcesUrl = useMemo(() => {
    const params = {
      page: page,
      page_size: PER_PAGE,
      utm_param: PARAMETER_MAP[parameter].key,
      browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if (dates) {
      params.start_date = dates.start.toISOString();
      params.end_date = dates.end.toISOString();
    }

    return `/api/v2/utm-parameters/top?${new URLSearchParams(params).toString()}`;
  }, [dates, page]);

  const {
    data: topParametersResponse,
    refetch: refetchTopParameters,
    isFetching: isFetchingTopParameters,
  } = useAppQuery({
    url: topSourcesUrl,
    extraQueryKeys: ["utmSources"],
    reactQueryOptions: {
      enabled: dates !== null,
      staleTime: 10 * 60 * 1000,
      placeholderData: keepPreviousData,
    },
  });

  const handleRefresh = () => {
    refetchTopParameters();
    refetchMetrics();
  };

  const error = topParametersResponse?.error || topMetrics?.error;
  const topParameters = topParametersResponse?.topParameters || [];

  let errorToRender;
  if (error && !isFetchingTopParameters) {
    errorToRender = (
      <Banner
        title="Error"
        tone="critical"
        action={{
          content: "Contact support",
          onAction: () => window.$crisp.push(["do", "chat:open"]),
        }}
      >
        <BlockStack gap="200">
          <Text as="p">
            There was an error fetching your analytics, please refresh.
          </Text>
        
          <Text as="p">
            If that doesn't work please reach out to support using the blue chat icon in the corner of your screen.
          </Text>
        </BlockStack>
      </Banner>
    );
  }

  let table;
  if (!error) {
    table = (
      <TopParametersTable
        parameters={topParameters}
        parameterKey={PARAMETER_MAP[parameter].key}
        totalCount={topMetrics.data[PARAMETER_MAP[parameter].key].totalCount}
        page={page}
        setPage={setPage}
        pageSize={PER_PAGE}
        resourceName={{ singular: PARAMETER_MAP[parameter].nameSingular, plural: PARAMETER_MAP[parameter].namePlural }}
        loading={isFetchingTopParameters}
        calculateParameterReportHref={PARAMETER_MAP[parameter].calculateReportHref}
      />
    );
  }

  return (
    <Page
      title={`Top ${PARAMETER_MAP[parameter].namePlural}`}
      backAction={{
        content: "Back to Dashboard",
        onAction: () => {
          navigate("/");
        },
      }}
      fullWidth
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {errorToRender}

            <InlineStack gap="200">
              <OrdersDatePicker />

              <Button
                icon={RefreshIcon}
                loading={isFetchingTopParameters}
                onClick={handleRefresh}
              >
                Refresh
              </Button>
            </InlineStack>

            {table}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
};
